import { useQuery } from '@apollo/client';
import { Button, Icon, Pill } from '@teamsnap/teamsnap-ui';
import * as RegistrationFormAdjustments from 'core/gql/queries/registration-form-adjustments';
import * as RegistrationFormVisibilityRules from 'core/gql/queries/registration-form-visibility-rules';
import * as React from 'react';
import ConfigureQuestionsDrawer from './ConfigureQuestionsDrawer';
import type { DraggableUniqueItem } from './DraggableList/DraggableList';
import { DraggableList } from './DraggableList/DraggableList';
import EditQuestionDrawer from './EditQuestionDrawer';
import FieldTypeDisplay from './FieldTypeDisplay';
import { FieldDefinitionType, FieldType } from './types';
import WarningModal from './WarningModal';

interface Props {
  orgId: number;
  formId: number;
  fields: FieldType[];
  organizationDefinitions: any[];
  addonsEnabled: boolean;
  onUpdate: (fields: FieldType[]) => void;
}

const noStyleBtn = {
  border: 'none',
  padding: 0,
  marginLeft: -14,
  backgroundColor: 'transparent',
};

const partnerAddonRequiredFieldNames = [
  'street_address',
  'city',
  'state',
  'zip',
];

const Questions = ({
  orgId,
  fields,
  organizationDefinitions,
  onUpdate,
  formId,
  addonsEnabled,
}: Props) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [activeField, setActiveField] = React.useState<FieldType | null>(null);
  const [deleteField, setDeleteField] = React.useState<FieldType | null>(null);

  const formFields = fields?.filter((f) => {
    const definition = organizationDefinitions.find(
      (d) => d.id === f.form_field_definition_id
    );
    return !f.internal && !(definition?.external_id || definition?.external_type)
  });

  const formVisibilityRules =
    useQuery(RegistrationFormVisibilityRules.QUERY, {
      variables: { formId: +formId },
      fetchPolicy: 'network-only',
      skip: !formId,
    })?.data?.registrationFormVisibilityRules || [];

  const formAdjustments =
    useQuery(RegistrationFormAdjustments.QUERY, {
      variables: { formId: +formId },
      fetchPolicy: 'network-only',
      skip: !formId,
    })?.data?.registrationFormAdjustments || [];

  React.useEffect(() => {
    // Add class bellow for modal-ception hack. Closing a modal remove need "scroll-lock" class
    document.getElementById('org')?.classList?.add('formbuilder-lock');
  }, []);

  React.useEffect(() => {
    if (!drawerOpen) {
      onUpdate(fields);
    }
  }, [drawerOpen]);

  const deleteQuestion = () => {
    const updatedFields = orderFieldsByTarget(
      formFields.filter((c) => c !== deleteField)
    );
    onUpdate(updatedFields);
    setDeleteField(null);
  };

  const reorderQuestions = (questions: DraggableUniqueItem<FieldType>[]) => {
    onUpdate(
      orderFieldsByTarget([
        ...formFields.map((formField) => {
          const movedRow = questions.findIndex((f) => f.item === formField);
          return {
            ...formField,
            row: movedRow === -1 ? formField.row : movedRow,
          };
        }),
      ])
    );
  };

  const lockFieldForAddons = (field: FieldType) =>
    addonsEnabled &&
    field.target === 'participant' &&
    partnerAddonRequiredFieldNames.includes(field.name);

  const renderFields = (title: string, fields: FieldType[]) => {
    const sortedFields = fields
      .filter((a) => a.enabled && !a.internal)
      .map((f) => (lockFieldForAddons(f) ? { ...f, locked: true } : f))
      .sort(sortByRow);

    return (
      <div data-testid={`${title} Section`}>
        {sortedFields.length > 0 && (
          <Pill mods="u-padLg u-spaceBottomSm u-flex u-flexCol">
            <p className="u-spaceBottomLg">{title}</p>
            <DraggableList
              items={sortedFields}
              onChange={reorderQuestions}
              renderItem={({ item: field, id }) => (
                <DraggableList.Item id={id}>
                  <div
                    key={`${field.target}-${field.form_field_definition_id}-${field.id}`}
                    data-testid={`field-${field.id}`}
                    className="u-bgNeutral1 u-spaceBottomSm"
                  >
                    <div className="Grid Grid--withGutter u-padMd u-flex u-flexAlignItemsCenter">
                      <div className="Grid-cell u-size1of24">
                        <DraggableList.DragHandle id={id}>
                          <Icon name="drag" />
                        </DraggableList.DragHandle>
                      </div>
                      <div className="Grid-cell u-size1of24">
                        <span>{field.question_number}</span>
                      </div>
                      <div className="Grid-cell u-size20of24">
                        <FieldTypeDisplay
                          field={field}
                          formId={formId}
                          hasFees={formAdjustments.some(
                            (a) => a.formFieldId === field.id
                          )}
                          hasLogic={formVisibilityRules.some(
                            (r) => r.formFieldId === field.id
                          )}
                        />
                      </div>
                      <div className="Grid-cell u-size1of24">
                        <Button
                          testId={`remove-question-${field.id}`}
                          style={noStyleBtn}
                          isDisabled={field.locked}
                          onClick={() => {
                            setDeleteField(field);
                          }}
                        >
                          <Icon name="trash" />
                        </Button>
                      </div>
                      <div className="Grid-cell u-size1of24">
                        <Button
                          testId={`edit-question-${field.id}`}
                          style={noStyleBtn}
                          onClick={() => {
                            setActiveField(field);
                          }}
                        >
                          <Icon name="settings" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </DraggableList.Item>
              )}
            />
          </Pill>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="u-flex u-flexCol">
        <div className="u-flex u-flexAlignSelfEnd u-spaceBottomMd">
          <Button
            type="button"
            mods="u-padSidesLg"
            color="primary"
            onClick={() => setDrawerOpen(!drawerOpen)}
            style={{ height: 44 }}
            testId="edit-questions-button"
          >
            Edit Questions
          </Button>
        </div>
        {formFields?.length > 0 && (
          <>
            {renderFields(
              'Participant Information',
              formFields.filter(
                (f) => f.target === 'participant' && !f.organization_id
              )
            )}
            {renderFields(
              'Guardian Information',
              formFields.filter(
                (f) => f.target === 'guardian' && !f.organization_id
              )
            )}
            {renderFields(
              'Additional Questions',
              formFields.filter((f) => f.organization_id)
            )}
          </>
        )}
        {drawerOpen && (
          <ConfigureQuestionsDrawer
            formId={formId}
            orgId={orgId}
            fields={formFields.map((f) =>
              lockFieldForAddons(f) ? { ...f, locked: true } : f
            )}
            onSave={(fields) => {
              onUpdate(fields);
            }}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          />
        )}
        {!!activeField && (
          <EditQuestionDrawer
            orgId={orgId}
            formId={formId}
            question={activeField}
            onClose={(updatedField) => {
              setActiveField(null);
            }}
          />
        )}
      </div>
      <WarningModal
        show={deleteField != null}
        onClose={() => {
          setDeleteField(null);
        }}
        onSubmit={() => {
          deleteQuestion();
        }}
      >
        <p className="u-padEndsSm">
          All conditional logic and fees will be lost. Are you sure you want to
          delete this question?
        </p>
      </WarningModal>
    </>
  );
};

export default Questions;

const defaultParticipantFieldNames = [
  'first_name',
  'last_name',
  'email',
  'birthdate',
  'gender',
  ...partnerAddonRequiredFieldNames,
];
const defaultGuardianFieldNames = [
  'first_name',
  'last_name',
  'email',
  'phone_number',
];
const sortByRow = (a: FieldType, b: FieldType) => a.row - b.row;
const sortByOrder = (
  a: FieldType | FieldDefinitionType,
  b: FieldType | FieldDefinitionType
) => a.order - b.order;
const reorderFieldsMap = (f: FieldType, i: number) => ({ ...f, row: i });

export const buildDefaultCheckedFields = (
  fieldDefinitions: FieldDefinitionType[]
): FieldType[] =>
  buildDefaultDefinitions(fieldDefinitions).map((d) => ({
    ...buildFieldFromDefinition(d),
    enabled: true,
  }));

export const hydrateFormFields = (
  fields: FieldType[],
  fieldDefinitions: FieldDefinitionType[]
): FieldType[] => {
  const hydratedFields = fields?.map((field) => {
    const definition = fieldDefinitions.find(
      (d) => d.id === field.form_field_definition_id
    );
    return {
      ...field,
      name: definition?.name,
      locked: definition?.locked,
      order: definition?.order,
      organization_id: definition?.organization_id,
    };
  });
  return orderFieldsByTarget(hydratedFields);
};

export const buildFieldFromDefinition = (
  definition: FieldDefinitionType
): FieldType => ({
  id: null,
  column: 0,
  row: 0,
  target: definition.target,
  required: definition.required_default,
  form_field_definition_id: definition.id,
  label: definition.label,
  name: definition.name,
  locked: definition.locked,
  type: definition.type,
  options: definition.options,
  group: null,
  organization_id: definition.organization_id,
  order: definition.order,
  multiselect: definition.multiselect,
  enabled: definition.enabled,
  question_number: 0,
  internal: false,
  helpText: '',
});

export const buildDefaultDefinitions = (
  fieldDefinitions: FieldDefinitionType[]
): FieldDefinitionType[] =>
  Array.from(
    new Set([
      ...fieldDefinitions.filter((d) => d.active && d.locked),
      ...fieldDefinitions.filter(
        (d) =>
          d.active &&
          defaultParticipantFieldNames.includes(d.name) &&
          d.target === 'participant'
      ),
      ...fieldDefinitions.filter(
        (d) =>
          d.active &&
          defaultGuardianFieldNames.includes(d.name) &&
          d.target === 'guardian'
      ),
    ])
  ).sort(sortByOrder);

export const orderFieldsByTarget = (fields: FieldType[]) => {
  let questionNumber = 0;
  const orderedFields = [
    ...fields
      .filter((c) => c.target === 'participant' && !c.organization_id)
      .sort(sortByRow)
      .map(reorderFieldsMap),
    ...fields
      .filter((c) => c.target === 'guardian' && !c.organization_id)
      .sort(sortByRow)
      .map(reorderFieldsMap),
    ...fields
      .filter((c) => c.organization_id)
      .sort(sortByRow)
      .map(reorderFieldsMap),
  ].map((f) => {
    if (f.enabled && !f.internal) {
      questionNumber++;
    }
    return { ...f, question_number: f.enabled ? questionNumber : null };
  });

  return orderedFields;
};
